// Verification dialog shown before ANY quote email is sent.
// Fetches /api/quotes/{id}/preview-email so staff can preview the exact
// HTML body and attachment list, then hit "Confirm & Send" (or Cancel).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency } from "@/lib/constants";
import { RecipientPicker } from "@/components/RecipientPicker";
import { AutoCcChips } from "@/components/AutoCcChips";
import { toast } from "sonner";
import { Send, Loader2, Eye, EyeOff, Paperclip, ShieldAlert, Mail, ExternalLink } from "lucide-react";

/** Show a Gmail-style "Sending in Ns · UNDO" toast that cancels the scheduled Resend email. */
function showUndoToast({ quoteId, resendId, seconds, email }) {
  let remaining = Math.max(1, seconds);
  const id = toast(
    (t) => (
      <div className="flex items-center gap-3" data-testid="undo-send-toast">
        <div className="flex-1 text-sm">
          <div className="font-semibold text-emerald-700">✉ Sending to {email}</div>
          <div className="text-xs text-slate-500">
            <span data-testid="undo-send-countdown">Delivers in {remaining}s</span> — press UNDO to cancel
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              await import("@/lib/api").then(({ api }) => api.post(`/quotes/${quoteId}/cancel-send`));
              toast.success("Send cancelled — the email won't be delivered.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Too late — email already sent.");
            } finally { toast.dismiss(t); }
          }}
          data-testid="undo-send-btn"
          className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm"
        >
          Undo
        </button>
      </div>
    ),
    { duration: seconds * 1000 + 500, dismissible: true }
  );
  // Live countdown
  const iv = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) { clearInterval(iv); return; }
    // sonner exposes toast() which can be updated by id
    const el = document.querySelector('[data-testid="undo-send-countdown"]');
    if (el) el.textContent = `Delivers in ${remaining}s`;
  }, 1000);
  return id;
}

/**
 * Props
 *   open       — bool, dialog open state
 *   onClose    — fn, called with (sent?: boolean); true if quote was sent
 *   quote      — { id, quote_number, customer_name, customer_email, sale_price }
 *   onSent     — optional fn(data) callback after successful send
 */
export default function ConfirmSendQuoteDialog({ open, onClose, quote, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [ccList, setCcList] = useState(null); // null = not loaded → backend applies defaults

  const loadAutoCc = async () => {
    try {
      const { data } = await api.get("/email/auto-cc");
      setCcList(data.quote_cc || []);
    } catch { setCcList(null); }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await api.get(`/quotes/${quote.id}/email-templates`);
      setTemplates(data.templates || []);
      const d = (data.templates || []).find((t) => t.default);
      if (d) {
        setTemplateId(d.id);
        setSubject((s) => s || d.subject || "");
        setMessage((m) => m || d.message || "");
      }
    } catch { setTemplates([]); }
  };
  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(id);
    setSubject(t.subject || "");
    setMessage(t.message || "");
  };

  const loadRecipients = async () => {
    try {
      const { data } = await api.get(`/quotes/${quote.id}/recipients`);
      setRecipientOptions(data.options || []);
      setSelectedEmails((data.options || []).filter((o) => o.default).map((o) => o.email));
    } catch {
      const e = (quote?.customer_email || "").toLowerCase();
      if (e) { setRecipientOptions([{ name: quote.customer_name || e, email: e, role: "On quote" }]); setSelectedEmails([e]); }
    }
  };

  const load = async (custom = false) => {
    if (!quote?.id) return;
    setLoading(true); setError(null);
    try {
      const body = custom ? { subject: subject || undefined, message: message || undefined } : {};
      const { data } = await api.post(`/quotes/${quote.id}/preview-email`, body);
      setPreview(data);
      if (!custom) setSubject(data.subject || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not build preview");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setShowPreview(false); setPreview(null); setError(null); setSubject(""); setMessage("");
      setRecipientOptions([]); setSelectedEmails([]); setTemplates([]); setTemplateId(""); setCcList(null);
      load(); loadRecipients(); loadTemplates(); loadAutoCc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote?.id]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, {
        subject: subject || undefined,
        message: message || undefined,
        to_emails: selectedEmails,
        cc_emails: ccList ? ccList.map((u) => u.email) : undefined,
      });
      if (data.status === "mocked") {
        toast.warning(`MOCK — email to ${data.email} logged only (Resend key not set).`);
      } else if (data.status === "failed") {
        toast.error(`Resend rejected: ${data.error || "delivery failed"}`);
      } else if (data.status === "scheduled" && data.resend_id) {
        // Undo Send toast — countdown + cancel button
        showUndoToast({
          quoteId: quote.id, resendId: data.resend_id, seconds: data.undo_seconds || 30, email: data.email,
        });
      } else {
        toast.success(`Quote emailed to ${data.email}`);
      }
      onSent?.(data);
      onClose?.(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send quote");
    } finally { setSending(false); }
  };

  const missingEmail = selectedEmails.length === 0;
  const totalInc = (quote?.sale_price || 0) * 1.10;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose?.(false)}>
      <DialogContent className={`rounded-sm max-h-[90vh] overflow-y-auto ${showPreview ? "sm:max-w-3xl" : "sm:max-w-md"}`} data-testid="confirm-send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Mail size={18} className="text-cyan-600" /> Verify &amp; send quote {quote?.quote_number}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Review the recipient, preview the email, then press Confirm &amp; Send. Nothing is delivered until you confirm.
          </DialogDescription>
        </DialogHeader>

        {/* Verification banner */}
        <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2.5 rounded-sm flex items-start gap-2" data-testid="confirm-send-banner">
          <ShieldAlert size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-snug">
            <b>Email is ready to send.</b> Nothing will be delivered until you press <b>Confirm &amp; Send</b>.
            Please review the recipient, quote details, and preview the message below.
          </div>
        </div>

        {/* Recipient picker + quote summary */}
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 space-y-2 text-xs">
          <div>
            <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">To</span>
            <div className="mt-1" data-testid="confirm-send-to">
              <RecipientPicker options={recipientOptions} selected={selectedEmails}
                onChange={setSelectedEmails}
                onAddCustom={(o) => setRecipientOptions((prev) => [...prev, o])}
                testPrefix="quote-recipient" />
            </div>
          </div>
          <AutoCcChips cc={ccList} testPrefix="quote-cc" toEmails={selectedEmails}
            onRemove={(email) => setCcList((prev) => (prev || []).filter((u) => u.email !== email))} />
          <div className="flex items-baseline gap-2 pt-1 border-t border-slate-200">
            <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold min-w-[70px]">Quote</span>
            <span className="text-slate-800">{quote?.quote_number} · <b>{fmtCurrency(quote?.sale_price)}</b> ex GST · <b>{fmtCurrency(totalInc)}</b> inc GST</span>
          </div>
          {preview?.attachments?.length > 0 && (
            <div className="flex items-baseline gap-2 pt-1 border-t border-slate-200">
              <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold min-w-[70px]">Attach.</span>
              <div className="flex flex-wrap gap-1 flex-1" data-testid="confirm-send-attachments">
                {preview.attachments.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-sm px-1.5 py-0.5 text-[10px] text-slate-600">
                    <Paperclip size={10} /> {a.filename}{a.size_bytes ? ` (${Math.round(a.size_bytes / 1024)} KB)` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          {preview?.mocked && (
            <div className="text-[10px] text-amber-700 pt-1 border-t border-slate-200">
              ⓘ Resend API key not configured — this email will be <b>logged only</b>, not delivered.
            </div>
          )}
        </div>

        {/* Editable subject + personal message (Trent, Jun 2026) */}
        <div className="space-y-2">
          {templates.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Template</div>
              <Select value={templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="quote-template-select">
                  <SelectValue placeholder="Choose a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`quote-template-opt-${t.id}`}>
                      {t.name}{t.default ? " (standard)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-400 mt-1">Picking a template fills the subject and message below — both stay editable. Admins manage templates under System → Email.</div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Subject</div>
            <Input data-testid="confirm-send-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="rounded-sm h-9 text-sm" maxLength={200} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Personal message (optional)</div>
            <Textarea data-testid="confirm-send-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              maxLength={2000} className="rounded-sm text-sm"
              placeholder="Leave blank to use the standard wording — anything typed here replaces the intro paragraph" />
          </div>
        </div>

        {/* Preview toggle */}
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" size="sm"
            onClick={async () => {
              if (!showPreview) { await load(true); setShowPreview(true); }
              else setShowPreview(false);
            }}
            disabled={loading}
            className="rounded-sm h-9" data-testid="preview-email-btn">
            {loading ? <Loader2 size={13} className="animate-spin mr-1.5" />
             : showPreview ? <EyeOff size={13} className="mr-1.5" />
                           : <Eye size={13} className="mr-1.5" />}
            {loading ? "Loading preview…" : showPreview ? "Hide message preview" : "Preview message"}
          </Button>
          {preview?.portal_url && (
            <a href={preview.portal_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] text-cyan-600 hover:underline inline-flex items-center gap-1"
              data-testid="preview-portal-link">
              Open portal link <ExternalLink size={11} />
            </a>
          )}
        </div>

        {error && (
          <div className="border border-rose-200 bg-rose-50 rounded-sm p-2.5 text-xs text-rose-800" data-testid="preview-email-error">
            {error}
          </div>
        )}

        {showPreview && preview?.html_body && (
          <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="email-preview-container">
            <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
              <span>Email preview — what the client will see</span>
              <span className="normal-case text-slate-400">Rendered from live template</span>
            </div>
            <iframe
              title="Quote email preview"
              srcDoc={preview.html_body}
              sandbox=""
              className="w-full h-[420px] bg-white"
              data-testid="email-preview-iframe"
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onClose?.(false)} disabled={sending} className="rounded-sm" data-testid="confirm-send-cancel-btn">
            Cancel
          </Button>
          <Button
            data-testid="confirm-send-quote-btn"
            onClick={send}
            disabled={sending || loading || missingEmail}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
            Confirm &amp; Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
